import { Box, Group, NumberInput, Stack, Switch, Text, Tooltip } from "@dutyon/ui-kit";
import { Info, Settings, Sparkles, Wand2 } from "lucide-react";
import { Controller, useForm } from "react-hook-form";

export interface DutyEditorSettingsValues {
  isFixedMode: boolean;
  showConsecutiveDutyTooltip: boolean;
  showAvoidanceDutyTooltip: boolean;
  showFiveDayConsecutiveTooltip: boolean;
  showPongDangPongDangTooltip: boolean;
  maxConsecutiveWorkDays: number;
  useForbiddenPatterns: boolean;
  useAvoidancePatterns: boolean;
  usePongDangPatterns: boolean;
}

interface DutyEditorSettingsFormProps {
  formId: string;
  initialValues: DutyEditorSettingsValues;
  onSubmit: (values: DutyEditorSettingsValues) => void;
}

export function DutyEditorSettingsForm({
  formId,
  initialValues,
  onSubmit,
}: DutyEditorSettingsFormProps) {
  const { register, handleSubmit, control } = useForm<DutyEditorSettingsValues>({
    defaultValues: initialValues,
  });

  return (
    <form id={formId} onSubmit={handleSubmit(onSubmit)}>
      <Stack gap="xl">
        {/* 에디터 설정 */}
        <Stack gap="xs">
          <Group
            gap="xs"
            style={{ borderBottom: "1px solid var(--mantine-color-gray-2)", paddingBottom: 6 }}
          >
            <Settings size={16} style={{ color: "var(--mantine-color-blue-filled)" }} />
            <Text fw={600} size="sm">
              에디터 설정
            </Text>
          </Group>
          <Box pt="xs">
            {/* 고정 모드 스위치 */}
            <Switch
              {...register("isFixedMode")}
              defaultChecked={initialValues.isFixedMode}
              label={
                <Group gap={4} align="center" wrap="nowrap">
                  <span>고정 모드</span>
                  <Tooltip
                    label={
                      <Stack gap={4}>
                        <Text size="xs" fw={600}>
                          고정 모드란?
                        </Text>
                        <Text size="xs">
                          활성화하면 셀을 클릭할 때마다 해당 근무가 <strong>고정</strong>으로
                          입력됩니다.
                        </Text>
                        <Text size="xs">고정된 셀은 자동 생성 시 덮어쓰지 않고 유지됩니다.</Text>
                      </Stack>
                    }
                    withArrow
                    multiline
                    w={260}
                    position="bottom"
                  >
                    <Info
                      size={14}
                      style={{
                        color: "var(--mantine-color-gray-5)",
                        cursor: "help",
                        flexShrink: 0,
                      }}
                    />
                  </Tooltip>
                </Group>
              }
              size="md"
            />
          </Box>
        </Stack>

        {/* 하이라이트 설정 */}
        <Stack gap="xs">
          <Group
            gap="xs"
            style={{ borderBottom: "1px solid var(--mantine-color-gray-2)", paddingBottom: 6 }}
          >
            <Sparkles size={16} style={{ color: "var(--mantine-color-teal-filled)" }} />
            <Text fw={600} size="sm">
              하이라이트 설정
            </Text>
          </Group>
          <Stack gap="md" pt="xs">
            {/* 연속근무 툴팁 (NE, ED, ND) */}
            <Switch
              {...register("showConsecutiveDutyTooltip")}
              defaultChecked={initialValues.showConsecutiveDutyTooltip}
              label={
                <Group gap={4} align="center" wrap="nowrap">
                  <span>연속근무 툴팁 (NE, ED, ND)</span>
                  <Tooltip
                    label="나이트-이브닝, 이브닝-데이 등 휴식이 부족한 근무 교대 패턴을 빨간색 테두리로 표시합니다."
                    withArrow
                    position="bottom"
                  >
                    <Info
                      size={14}
                      style={{
                        color: "var(--mantine-color-gray-5)",
                        cursor: "help",
                        flexShrink: 0,
                      }}
                    />
                  </Tooltip>
                </Group>
              }
              size="md"
            />

            {/* 기피근무 여부 (NNN, NOD, NOE) */}
            <Switch
              {...register("showAvoidanceDutyTooltip")}
              defaultChecked={initialValues.showAvoidanceDutyTooltip}
              label={
                <Group gap={4} align="center" wrap="nowrap">
                  <span>기피근무 여부 (NNN, NOD, NOE)</span>
                  <Tooltip
                    label="나이트 3회 연속, 나이트-오프-데이 등 기피되는 근무 패턴을 보라색 테두리로 표시합니다."
                    withArrow
                    position="bottom"
                  >
                    <Info
                      size={14}
                      style={{
                        color: "var(--mantine-color-gray-5)",
                        cursor: "help",
                        flexShrink: 0,
                      }}
                    />
                  </Tooltip>
                </Group>
              }
              size="md"
            />

            {/* 5일 이상 연속 근무 여부 */}
            <Switch
              {...register("showFiveDayConsecutiveTooltip")}
              defaultChecked={initialValues.showFiveDayConsecutiveTooltip}
              label={
                <Group gap={4} align="center" wrap="nowrap">
                  <span>5일 이상 연속 근무 여부</span>
                  <Tooltip
                    label="동일한 근무가 5일 연속으로 배치되었을 때 빨간 테두리로 표시합니다."
                    withArrow
                    position="bottom"
                  >
                    <Info
                      size={14}
                      style={{
                        color: "var(--mantine-color-gray-5)",
                        cursor: "help",
                        flexShrink: 0,
                      }}
                    />
                  </Tooltip>
                </Group>
              }
              size="md"
            />

            {/* 퐁당퐁당 여부 (NONO) */}
            <Switch
              {...register("showPongDangPongDangTooltip")}
              defaultChecked={initialValues.showPongDangPongDangTooltip}
              label={
                <Group gap={4} align="center" wrap="nowrap">
                  <span>퐁당퐁당 여부 (NONO)</span>
                  <Tooltip
                    label="나이트-오프-나이트-오프와 같이 불규칙한 패턴을 남색 테두리로 표시합니다."
                    withArrow
                    position="bottom"
                  >
                    <Info
                      size={14}
                      style={{
                        color: "var(--mantine-color-gray-5)",
                        cursor: "help",
                        flexShrink: 0,
                      }}
                    />
                  </Tooltip>
                </Group>
              }
              size="md"
            />
          </Stack>
        </Stack>

        {/* 생성 옵션 설정 */}
        <Stack gap="xs">
          <Group
            gap="xs"
            style={{ borderBottom: "1px solid var(--mantine-color-gray-2)", paddingBottom: 6 }}
          >
            <Wand2 size={16} style={{ color: "var(--mantine-color-violet-filled)" }} />
            <Text fw={600} size="sm">
              생성 옵션 설정
            </Text>
          </Group>
          <Box pt="xs" px="xs">
            <Stack gap="md">
              <Controller
                name="maxConsecutiveWorkDays"
                control={control}
                render={({ field }) => (
                  <NumberInput
                    label="최대 연속 근무 제한"
                    description="간호사가 연속으로 일할 수 있는 최대 일수를 설정합니다."
                    min={3}
                    max={14}
                    size="sm"
                    style={{ maxWidth: 280 }}
                    value={field.value}
                    onChange={(val) => field.onChange(typeof val === "number" ? val : 6)}
                  />
                )}
              />

              <Switch
                {...register("useForbiddenPatterns")}
                defaultChecked={initialValues.useForbiddenPatterns}
                label={
                  <Group gap={4} align="center" wrap="nowrap">
                    <span>금지 근무 패턴(NE, ED, ND) 차단</span>
                    <Tooltip
                      label="야간-이브닝, 이브닝-데이, 야간-데이 등 간호사의 휴식이 현저히 부족한 연속 근무 배정을 자동으로 차단합니다."
                      withArrow
                      position="bottom"
                    >
                      <Info
                        size={14}
                        style={{
                          color: "var(--mantine-color-gray-5)",
                          cursor: "help",
                          flexShrink: 0,
                        }}
                      />
                    </Tooltip>
                  </Group>
                }
                size="md"
              />

              <Switch
                {...register("useAvoidancePatterns")}
                defaultChecked={initialValues.useAvoidancePatterns}
                label={
                  <Group gap={4} align="center" wrap="nowrap">
                    <span>기피 근무 패턴(NNN, NOD, NOE) 차단</span>
                    <Tooltip
                      label="3일 연속 야간 근무(NNN) 및 야간 후 하루 쉬고 주/야간 배치(NOD, NOE) 등 극도의 피로를 유발하는 근무 패턴 배정을 자동으로 차단합니다."
                      withArrow
                      position="bottom"
                    >
                      <Info
                        size={14}
                        style={{
                          color: "var(--mantine-color-gray-5)",
                          cursor: "help",
                          flexShrink: 0,
                        }}
                      />
                    </Tooltip>
                  </Group>
                }
                size="md"
              />

              <Switch
                {...register("usePongDangPatterns")}
                defaultChecked={initialValues.usePongDangPatterns}
                label={
                  <Group gap={4} align="center" wrap="nowrap">
                    <span>퐁당퐁당 패턴(NONO) 차단</span>
                    <Tooltip
                      label="야간 근무 후 하루 쉬고 다시 야간 근무에 배치되는 퐁당퐁당(N-OFF-N-OFF) 패턴 배정을 자동으로 차단합니다."
                      withArrow
                      position="bottom"
                    >
                      <Info
                        size={14}
                        style={{
                          color: "var(--mantine-color-gray-5)",
                          cursor: "help",
                          flexShrink: 0,
                        }}
                      />
                    </Tooltip>
                  </Group>
                }
                size="md"
              />
            </Stack>
          </Box>
        </Stack>
      </Stack>
    </form>
  );
}
